import dayjs from "dayjs";
import type {
  PlayerCardData,
  PlayerCardInjury,
  PlayerCardOption,
  PlayerCardStatusEntry,
} from "../../Processing/types";

const SAMPLE_LAST_5_INJURIES: PlayerCardInjury[] = [
  injury(0, "2026-06-08", "2026-06-13", "Forearm (Right)", "Accident"),
  injury(0, "2026-05-20", "2026-05-27", "Knee (Right)", "Existing"),
  injury(0, "2026-04-15", "2026-04-18", "Glute", "Contact (w/ Other)"),
  injury(0, "2026-03-10", "2026-03-20", "Ankle (Left)", "Contact"),
  injury(0, "2026-02-22", "2026-02-28", "Chin", "Contact (w/ Surface)"),
];

// Active injuries are open (no end date) and show a relative "N days ago", so the
// start date is computed from today to stay accurate.
function sampleActiveInjuries(): PlayerCardInjury[] {
  return [
    injury(0, dayjs().subtract(4, "day").format("YYYY-MM-DD"), null, "Shoulder (Left)", "Contact"),
  ];
}

function injury(
  injury_id: number,
  injury_start_date: string,
  injury_end_date: string | null,
  injury_location: string,
  injury_type: string,
): PlayerCardInjury {
  return {
    injury_id,
    team_id_id: 146,
    player_id_id: 21661,
    injury_start_date,
    recovery_start_date: null,
    injury_end_date,
    injury_status: "injured",
    injury_type,
    is_soft_tissue: "N/A",
    injury_location,
    is_surgery: "N/A",
    is_referral: "N/A",
    is_image: "N/A",
    is_season_ending: "N/A",
    activity: "N/A",
    drill: "N/A",
    games_lost: 0,
    metadata: {},
    external_reference: null,
  };
}

const SAMPLE_DATES = ["2026-06-09", "2026-06-10", "2026-06-11", "2026-06-12", "2026-06-13"];

// Sample statuses paired to SAMPLE_DATES in chronological order (renders left→right).
const SAMPLE_AVAIL: { name: string; color: string }[] = [
  { name: "Full", color: "#e2efdc" },
  { name: "Full", color: "#e2efdc" },
  { name: "Limited", color: "#ffe0b3" },
  { name: "Out", color: "#ffcccc" },
  { name: "Full", color: "#e2efdc" },
];

function sampleAvailability(): Record<string, PlayerCardStatusEntry> {
  const out: Record<string, PlayerCardStatusEntry> = {};
  SAMPLE_DATES.forEach((date, i) => {
    const { name, color } = SAMPLE_AVAIL[i];
    out[date] = { player_id: 0, date, status_id: i + 1, name, color, metadata: {} };
  });
  return out;
}

/**
 * Build sample PlayerCardData for the configurator preview (no fetch happens there).
 * Real player name/position are used; value-bearing fields show neutral placeholders.
 */
export function buildPreviewPlayerCardData(
  options: PlayerCardOption[],
  playerName: string | null,
  playerPosition: string | null,
): PlayerCardData {
  const has = (o: PlayerCardOption) => options.includes(o);
  const data: PlayerCardData = {};
  if (has("name")) data.name = playerName ?? "Player Name";
  if (has("headshot")) data.headshot = ""; // empty → silhouette fallback in preview
  if (has("position")) data.position = playerPosition || "Position";
  if (has("jersey")) data.jersey = 0;
  if (has("top_speed")) data.top_speed = 21.3;
  if (has("bodyweight_baseline")) data.bodyweight_baseline = 255;
  if (has("last_5_status")) data.last_5_status = sampleAvailability();
  if (has("active_injuries")) data.active_injuries = sampleActiveInjuries();
  if (has("last_5_injuries")) data.last_5_injuries = SAMPLE_LAST_5_INJURIES;
  return data;
}
